import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../../public/icons/Icon.tsx'

interface TooltipProps {
	text?: string
	imagePreview?: string
	videoPreview?: string
	children: React.ReactNode
	delay?: number
	maxWidth?: number
	/** Макс. сторона превью-картинки, px (по умолчанию 240 — как в виджете).
	 *  Кабинет персонажа передаёт крупнее, чтобы рассмотреть фото детально. */
	imageMax?: number
}

interface TooltipPos {
	top: number
	left: number
	arrowLeft: number
	openBelow: boolean
}

const IMAGE_PREVIEW_MAX = 240
const IMAGE_PREVIEW_PLACEHOLDER_W = 160

function calcTooltipPos(el: HTMLElement, tooltipW: number): TooltipPos {
	const rect = el.getBoundingClientRect()
	const gap = 8
	const arrowH = 6
	const vw = window.innerWidth
	const openBelow = rect.top < 80
	const idealLeft = rect.left + rect.width / 2 - tooltipW / 2
	const left = Math.max(8, Math.min(idealLeft, vw - tooltipW - 8))
	const arrowLeft = Math.max(12, Math.min(rect.left + rect.width / 2 - left - 6, tooltipW - 24))
	const top = openBelow ? rect.bottom + gap + arrowH : rect.top - gap - arrowH
	return { top, left, arrowLeft, openBelow }
}

function VideoWithFallback({ src }: { src: string }) {
	const [error, setError] = useState(false)

	if (error) {
		return (
			<div className="tooltip__video-error">
				<Icon id="gen-video-error" size={32} />
			</div>
		)
	}

	return (
		<video
			src={src}
			className="tooltip__image-preview"
			muted
			playsInline
			autoPlay
			loop
			preload="auto"
			onLoadedMetadata={(e) => {
				e.currentTarget.currentTime = 0
			}}
			onError={() => setError(true)}
		/>
	)
}

export default function Tooltip({
	text,
	imagePreview,
	videoPreview,
	children,
	delay = 400,
	maxWidth = 260,
	imageMax = IMAGE_PREVIEW_MAX,
}: TooltipProps) {
	const [show, setShow] = useState(false)
	const [visible, setVisible] = useState(false)
	const [pos, setPos] = useState<TooltipPos | null>(null)
	const triggerRef = useRef<HTMLSpanElement>(null)
	const imgRef = useRef<HTMLImageElement>(null)
	const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	const isImageMode = !!imagePreview
	const isVideoMode = !!videoPreview
	const isMediaMode = isImageMode || isVideoMode
	const effectiveDelay = isMediaMode ? 200 : delay
	const effectiveWidth = isMediaMode ? IMAGE_PREVIEW_PLACEHOLDER_W : maxWidth

	const hasMovedRef = useRef(false)

	const open = useCallback(() => {
		if (!hasMovedRef.current) return
		if (hideTimer.current) clearTimeout(hideTimer.current)
		showTimer.current = setTimeout(() => {
			if (!triggerRef.current) return
			setPos(calcTooltipPos(triggerRef.current, effectiveWidth))
			setShow(true)
			requestAnimationFrame(() => setVisible(true))
		}, effectiveDelay)
	}, [effectiveDelay, effectiveWidth])

	const close = useCallback(() => {
		if (showTimer.current) clearTimeout(showTimer.current)
		setVisible(false)
		hideTimer.current = setTimeout(() => setShow(false), 150)
	}, [])

	const handleImageLoad = useCallback(() => {
		if (!triggerRef.current || !imgRef.current) return
		const naturalW = imgRef.current.naturalWidth
		const naturalH = imgRef.current.naturalHeight
		const scale = Math.min(imageMax / naturalW, imageMax / naturalH, 1)
		setPos(calcTooltipPos(triggerRef.current, Math.round(naturalW * scale)))
	}, [imageMax])

	useEffect(() => {
		if (!show) return
		function update() {
			if (!triggerRef.current) return
			let w = effectiveWidth
			if (imgRef.current?.naturalWidth && imgRef.current?.naturalHeight) {
				const scale = Math.min(
					imageMax / imgRef.current.naturalWidth,
					imageMax / imgRef.current.naturalHeight,
					1,
				)
				w = Math.round(imgRef.current.naturalWidth * scale)
			}
			setPos(calcTooltipPos(triggerRef.current, w))
		}
		window.addEventListener('scroll', update, true)
		window.addEventListener('resize', update)
		return () => {
			window.removeEventListener('scroll', update, true)
			window.removeEventListener('resize', update)
		}
	}, [show, effectiveWidth, imageMax])

	useEffect(
		() => () => {
			if (showTimer.current) clearTimeout(showTimer.current)
			if (hideTimer.current) clearTimeout(hideTimer.current)
		},
		[],
	)

	if (!text && !imagePreview && !videoPreview) {
		return <span className="tooltip-trigger">{children}</span>
	}

	const tooltip =
		show && pos
			? createPortal(
					<div
						className={`tooltip ${visible ? 'tooltip--visible' : ''} ${pos.openBelow ? 'tooltip--below' : 'tooltip--above'} ${isMediaMode ? 'tooltip--image-preview' : ''}`}
						style={{
							position: 'fixed',
							top: pos.openBelow ? pos.top : undefined,
							bottom: pos.openBelow ? undefined : window.innerHeight - pos.top,
							left: pos.left,
							maxWidth: isMediaMode ? imageMax : maxWidth,
							zIndex: 10000,
							// Крупное превью в кабинете: img читает эту переменную (CSS
							// иначе жёстко режет картинку 240px).
							...(isImageMode ? { '--tt-img-max': `${imageMax}px` } : {}),
						} as React.CSSProperties}
						role="tooltip"
					>
						<div className="tooltip__arrow" style={{ left: pos.arrowLeft }} />
						{isImageMode ? (
							<img
								ref={imgRef}
								src={imagePreview}
								alt="Preview"
								className="tooltip__image-preview"
								onLoad={handleImageLoad}
							/>
						) : isVideoMode ? (
							<VideoWithFallback src={videoPreview!} />
						) : (
							text
						)}
					</div>,
					document.body,
				)
			: null

	return (
		<span
			ref={triggerRef}
			className="tooltip-trigger"
			onMouseEnter={() => {
				hasMovedRef.current = true
				open()
			}}
			onMouseMove={() => {
				hasMovedRef.current = true
			}}
			onMouseLeave={() => {
				hasMovedRef.current = false
				close()
			}}
			onFocus={open}
			onBlur={close}
		>
			{children}
			{tooltip}
		</span>
	)
}
